import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { PenTool, X, Loader } from "lucide-react";
import AuthenticationService from "../../_services/AuthenticationService";
import ApiService from "../../_services/ApiService";
import WhiteboardManager from "../../pages/class-review-dashboard/WhiteboardManager";
import "./ToolLauncher.css";

/**
 * Global "Live Tools" launcher.
 *
 * A floating button that lets an instructor start a live whiteboard for one of
 * their batches WITHOUT going through a flip class. Only rendered for logins
 * linked to an instructor (currentUser.instructorId) — admins/counselors never
 * see it. The board is batch-scoped (stepId 0 = standalone), and every student
 * in that batch sees it instantly via the app-wide WhiteboardOverlay in ilearn.
 *
 * Mirrors the AccessibilityToggle FAB pattern (fixed position, outside-click +
 * Escape close). Reuses the existing WhiteboardManager modal unchanged.
 */

interface BatchOption {
  id: number;
  name: string;
}

const PUBLIC_PREFIXES = ["/login", "/unauthorized"];

const ToolLauncher: React.FC = () => {
  const location = useLocation();
  const currentUser = AuthenticationService.currentUserValue as any;
  const instructorId: number | undefined = currentUser?.instructorId;

  const [open, setOpen] = useState(false);
  const [batches, setBatches] = useState<BatchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [whiteboardBatchId, setWhiteboardBatchId] = useState<number | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const loadedRef = useRef(false);

  // Load the teacher's batches the first time the popover opens.
  useEffect(() => {
    if (!open || loadedRef.current) return;
    loadedRef.current = true;
    if (!instructorId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await ApiService.syncBatches(instructorId);
        const raw: any[] = res.data?.data || [];
        const seen = new Set<number>();
        const list: BatchOption[] = [];
        for (const b of raw) {
          const id = Number(b.id);
          if (seen.has(id)) continue;
          seen.add(id);
          list.push({ id, name: b.name });
        }
        setBatches(list);
        if (list.length === 1) setSelectedBatchId(list[0]!.id);
      } catch {
        setError("Could not load your batches. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, instructorId]);

  // Outside-click + Escape to close the popover.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isPublic = PUBLIC_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  );
  if (!currentUser || !instructorId || isPublic) return null;

  const startWhiteboard = () => {
    if (!selectedBatchId) return;
    setWhiteboardBatchId(selectedBatchId);
    setOpen(false);
  };

  return (
    <>
      <div className="tool-launcher" ref={containerRef}>
        {open && (
          <div className="tl-panel" role="dialog" aria-label="Live class tools">
            <div className="tl-panel-head">
              <span className="tl-panel-title">Live Tools</span>
              <button
                type="button"
                className="tl-panel-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
            <p className="tl-panel-hint">
              Start a live whiteboard for a batch — no flip class needed. Students in the
              batch see it instantly.
            </p>
            <label className="tl-field-label" htmlFor="tl-batch">
              Batch
            </label>
            {loading ? (
              <div className="tl-loading">
                <Loader size={16} className="tl-spin" /> Loading your batches…
              </div>
            ) : error ? (
              <div className="tl-error">{error}</div>
            ) : batches.length === 0 ? (
              <div className="tl-error">No batches are assigned to you.</div>
            ) : (
              <select
                id="tl-batch"
                className="tl-select"
                value={selectedBatchId ?? ""}
                onChange={(e) =>
                  setSelectedBatchId(e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">Select a batch…</option>
                {batches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="button"
              className="tl-start-btn"
              onClick={startWhiteboard}
              disabled={!selectedBatchId}
            >
              <PenTool size={16} /> Start Whiteboard
            </button>
          </div>
        )}
        <button
          type="button"
          className="tl-fab"
          onClick={() => setOpen((v) => !v)}
          aria-label="Live class tools"
          aria-expanded={open}
          title="Live class tools"
        >
          <PenTool size={22} />
        </button>
      </div>

      {whiteboardBatchId != null && (
        <WhiteboardManager
          stepId={0}
          batchId={whiteboardBatchId}
          isOpen={whiteboardBatchId != null}
          onClose={() => setWhiteboardBatchId(null)}
        />
      )}
    </>
  );
};

export default ToolLauncher;
